/**
 * API client for the search endpoint.
 *
 * Contract (TC-017): GET /api/v1/schools/search
 * Returns an array of SearchResult objects.
 *
 * WI-19: Added serializeFilters helper — single source of truth for
 *   SWR cache key, sessionStorage scroll key, and URL param serialization.
 *
 * 14d: Removed `major` field from SearchFilters and serializeFilters.
 *   The backend does not capture major data, so the filter had no effect.
 *
 * WI-38: Added `state?: string[]` to SearchFilters. serializeFilters now
 *   includes `state` after `region` in deterministic param order
 *   (sport, position, division, region, state, target_year).
 */

/**
 * Coarse-parent fallback score returned when a school has no fine-position data.
 * WI-26 (position fallback scoring).
 */
export interface FallbackScore {
  /** Pre-computed coarse parent opening score (0–100). */
  score: number;
  /** Coarse position slug (e.g. "fwd"). */
  position: string;
  /** Coarse position display name (e.g. "Forward"). */
  position_name: string;
}

export interface SearchResult {
  school_id: string;
  school_name: string;
  school_slug: string;
  division: string;
  region: string;
  state: string;
  /** Per-position mode (1+ chips selected): present. SUM mode (0 chips): absent. */
  score?: number;
  /** "high" | "medium" | "low" — always present. */
  tier: 'high' | 'medium' | 'low';
  /** Per-position mode: present (slug like "lw"). SUM mode: absent. */
  winning_position?: string;
  /** SUM mode (0 chips): present. Per-position mode: absent. */
  total_score?: number;
  /**
   * True when at least one player in the latest roster snapshot is mapped to
   * the winning position. Only present in per-position mode. WI-26.
   */
  has_data?: boolean;
  /**
   * Coarse parent score when has_data is false and the winning position is a fine position.
   * Absent when not applicable (coarse position queried, has_data true, or no coarse parent score).
   * WI-26.
   */
  fallback_score?: FallbackScore;
  scraped_at: string | null;
}

export interface SearchFilters {
  sport?: string;
  position?: string[];
  /**
   * Division slug(s). Multi-select OR filter. WI-43.
   * Serializes as comma-separated string in URL (e.g. `division=ncaa_d1,ncaa_d2`).
   * Backend accepts both a single value and a comma-separated list.
   */
  division?: string[];
  region?: string;
  /** Two-letter state code(s). Multi-select OR filter. WI-38. */
  state?: string[];
  target_year?: number;
}

/**
 * WI-19: Serialize a SearchFilters object to a deterministic string suitable
 * for use as a cache key or sessionStorage key.
 *
 * Rules:
 *  - Undefined/empty params are omitted.
 *  - Position slugs are sorted alphabetically.
 *  - State codes are sorted alphabetically. WI-38.
 *  - Params are emitted in a fixed order: sport, position, division, region, state, target_year.
 *
 * This is the single source of truth — used by useSearch (SWR cache), SearchPage
 * (scroll save/restore), and ResultCard/handleSubmit (URL writes).
 */
export function serializeFilters(filters: SearchFilters): string {
  const params = new URLSearchParams();
  if (filters.sport) params.set('sport', filters.sport);
  if (filters.position && filters.position.length > 0) {
    params.set('position', [...filters.position].sort().join(','));
  }
  if (filters.division && filters.division.length > 0) {
    params.set('division', [...filters.division].sort().join(','));
  }
  if (filters.region) params.set('region', filters.region);
  if (filters.state && filters.state.length > 0) {
    params.set('state', [...filters.state].sort().join(','));
  }
  if (filters.target_year) params.set('target_year', String(filters.target_year));
  return params.toString();
}

/**
 * Call the search API and return the array of matching schools.
 * Always returns an array (empty array on no match — TC-002).
 * Throws on network or server error.
 */
export async function searchSchools(
  filters: SearchFilters,
  signal?: AbortSignal,
): Promise<SearchResult[]> {
  const query = serializeFilters(filters);
  const url = `/api/v1/schools/search?${query}`;
  const response = await fetch(url, signal ? { signal } : undefined);

  if (!response.ok) {
    throw new Error(`Search failed: ${response.status} ${response.statusText}`);
  }

  const data: SearchResult[] = await response.json();
  return data;
}

// ── Score tier helpers ────────────────────────────────────────────────────────

/**
 * Returns the Tailwind color class for the score dot.
 *
 * Phase 1 fix: corrected WCAG AA contrast failures.
 * green-700 (6.0:1), amber-700 (5.1:1), red-600 (5.9:1) — all pass AA on white.
 * Previous values green-500 (2.2:1), amber-500 (2.3:1), gray-400 (2.8:1) all failed.
 */
export function tierColor(tier: SearchResult['tier']): string {
  switch (tier) {
    case 'high': return 'text-green-700';
    case 'medium': return 'text-amber-700';
    case 'low': return 'text-red-600';
  }
}

/**
 * Returns the Tailwind left-border color class for a result card tier accent.
 *
 * Phase 4a: tier-colored 4px left border on ResultCard. Uses the same semantic
 * tier colors as tierColor() for visual consistency — the border echoes the score
 * badge so the tier hue appears twice per card.
 *
 * Contrast (non-text, WCAG 1.4.11 AA, 3:1 minimum):
 *   green-700 (#15803D) on white: 6.0:1 — passes AA.
 *   amber-700 (#B45309) on white: 5.1:1 — passes AA.
 *   red-600   (#DC2626) on white: 5.9:1 — passes AA.
 *
 * Usage: apply `border-l-4` plus this class to the Card element.
 */
export function borderColorClass(tier: SearchResult['tier']): string {
  switch (tier) {
    case 'high':   return 'border-l-green-700';
    case 'medium': return 'border-l-amber-700';
    case 'low':    return 'border-l-red-600';
  }
}

/**
 * Returns the warm display label for the score tier (FR-010: color is never sole indicator).
 *
 * Phase 1 fix: warm/encouraging labels replace clinical abbreviations per design system.
 * "Great odds" / "Some openings" / "Longer odds" — honest without being alarming.
 */
export function tierLabel(tier: SearchResult['tier']): string {
  switch (tier) {
    case 'high': return 'Great odds';
    case 'medium': return 'Some openings';
    case 'low': return 'Longer odds';
  }
}

/**
 * Returns a human-readable "Last updated N days ago" string from a scraped_at
 * ISO timestamp. Returns "Unknown" if scraped_at is null.
 */
export function formatScrapedAt(scraped_at: string | null): string {
  if (!scraped_at) return 'Unknown';
  const scraped = new Date(scraped_at);
  const now = new Date();
  const diffMs = now.getTime() - scraped.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  if (diffDays === 0) return 'Updated today';
  if (diffDays === 1) return 'Updated 1 day ago';
  return `Updated ${diffDays} days ago`;
}
